import React, { useEffect, useState } from "react";
import IconEye from "../../../components/icons/IconEye";
import apiRoutes from "../../../api/apiRoutes";
import { appFetchJson } from "../../../utils/http";
import { shouldRunOnce } from "../../../utils/devOnce";

// Componentes de diseño
import ListHeader from "../../../components/listing/ListHeader";
import ListFooter from "../../../components/listing/ListFooter";
import DynamicTable from "../../../components/listing/DynamicTable";
import useEscClose from "../../useEscClose";
import FiltroTransaccionesBancard from "./FiltroTransaccionesBancard";
import VerTransaccionesBancard from "./VerTransaccionesBancard";

const DEFAULT_PAGE_SIZE = 30;

// Helper Fecha (Reutilizado de Auditoría)
const formatoFechaHora = (fecha) => {
  if (!fecha) return "";
  const d = new Date(fecha);
  if (isNaN(d.getTime())) return fecha; // Si ya viene texto, devolverlo
  try {
    return d.toLocaleString("es-PY", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit"
    });
  } catch (e) {
    return fecha;
  }
};

// Helper Monto (Guaraníes)
const formatoMonto = (valor) => {
  if (valor === null || valor === undefined) return "";
  
  // Si es un número, lo formateamos
  if (typeof valor === 'number') {
    return new Intl.NumberFormat('es-PY').format(valor);
  }
  
  // Si es string, asumimos que ya viene o puede venir con puntos.
  // Si quisieras forzar re-formateo de un string numérico: parseInt(valor)
  return valor; 
};

export default function ListarTransaccionesBancard() {
  // ===========================
  //       ESTADOS Y DATA
  // ===========================
  const [items, setItems] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [paginaActual, setPaginaActual] = useState(1);
  const [porPagina, setPorPagina] = useState(DEFAULT_PAGE_SIZE);
  const [totalBackend, setTotalBackend] = useState(0);

// ===========================
//       ESTADOS Y FILTROS
// ===========================
// Objeto con los filtros activos (puede contener q, fecha, etc.)
const [filtros, setFiltros] = useState({});

// Controla la visibilidad del drawer lateral de filtros
const [mostrarFiltros, setMostrarFiltros] = useState(false);

// Cierra el drawer de filtros al presionar la tecla ESC (solo si está abierto)
useEscClose(() => setMostrarFiltros(false), mostrarFiltros);

// ===========================
//       MODAL DE DETALLE
// ===========================
// Almacena el ID del registro seleccionado para ver en detalle.
// Si es null, el modal está cerrado; si tiene un valor, se abre el modal.
const [idSeleccionado, setIdSeleccionado] = useState(null);

// Cierra el modal de detalle al presionar la tecla ESC (solo si está abierto)
  useEscClose(() => setIdSeleccionado(null), idSeleccionado != null);

  // ===========================
  //       FETCH LISTADO
  // ===========================
  const fetchList = async (
    pageIndex = paginaActual - 1,
    size = porPagina,
    filtrosAplicados = filtros
  ) => {
    setCargando(true);
    try {
      const url = apiRoutes.transaccionBancard.listUrl({
        page: pageIndex,
        size: size,
        ...filtrosAplicados
      });
      
      const data = await appFetchJson(url);
      const contenido = data?.items || data?.content || [];
      const total = data?.totalElements ?? data?.total ?? contenido.length;

      setItems(contenido);
      setTotalBackend(total);
    } catch (err) {
      console.error("Error al obtener transacciones:", err);
      setItems([]);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    if (!shouldRunOnce("ListarTransacciones:init")) return;
    fetchList(0, porPagina, filtros);
  }, []);

  // ===========================
  //       ACCIONES
  // ===========================
  const cambiarPagina = (num) => {
    setPaginaActual(num);
    fetchList(num - 1, porPagina, filtros);
  };

  const manejarCambioCantidad = (size) => {
    setPorPagina(size);
    setPaginaActual(1);
    fetchList(0, size, filtros);
  };

// ====================================================================
// DEFINICIÓN DE COLUMNAS (Ajustado a Píxeles fijos para evitar colapso)
// ====================================================================
const columns = [
  // 1. Nro. Boleta
  { 
    header: "Nro. Boleta", 
    accessor: "numeroComprobante", 
    type: "text", 
    width: "90px", // Píxeles fijos
    cellClass: "text-xs font-mono text-gray-700" 
  },
  // 2. Nro. Remito
  { 
    header: "Nro. Remito", 
    accessor: "numeroRemito", 
    type: "text", 
    width: "100px", 
    cellClass: "text-xs font-mono text-gray-600" 
  },
  // 3. Nro. Tarjeta
  { 
    header: "Nro. Tarjeta", 
    accessor: "numeroTarjeta", 
    type: "text", 
    width: "110px", 
    cellClass: "text-xs font-mono text-gray-600" 
  },
  // 4. Cod. Comercio
  { 
    header: "Cód. Comercio", className: "whitespace-normal", 
    accessor: "codigoComercio", 
    type: "center", 
    width: "70px", 
    cellClass: "text-xs text-center text-gray-600" 
  },
  // 5. C.Prod1 (Aumentado para que el encabezado quepa bien)
  { 
    header: "C.Prod1", 
    accessor: "codigoProductofixme", 
    type: "center", 
    width: "80px", 
    cellClass: "text-xs text-center text-gray-500" 
  },
  // 6. Cant.1
  { 
    header: "Cant.1", 
    accessor: "qnProductofixme", 
    type: "center", 
    width: "60px", 
    cellClass: "text-xs text-center font-semibold" 
  },
  // 7. C.Prod2
  { 
    header: "C.Prod2", 
    accessor: "codigoProductofixme1", 
    type: "center", 
    width: "80px", 
    cellClass: "text-xs text-center text-gray-500" 
  },
  // 8. Cant.2
  { 
    header: "Cant.2", 
    accessor: "qnProductofixme1", 
    type: "center", 
    width: "60px", 
    cellClass: "text-xs text-center font-semibold" 
  },
  // 9. Fecha
  { 
    header: "Fecha Transacción", className: "whitespace-normal", 
    accessor: "dataTransaccion", 
    type: "text", 
    width: "130px", // Espacio suficiente para fecha y hora
    cellClass: "text-xs text-gray-700 leading-tight pl-2", 
    render: (row) => formatoFechaHora(row.dataTransaccion)
  },
  // 10. Monto (Alineado a derecha para números suele ser mejor, pero mantuve tu estilo)
  { 
    header: "Monto", 
    accessor: "valorTransaccion", 
    type: "text", 
    width: "90px", 
    cellClass: "text-xs font-bold text-gray-800 whitespace-nowrap", 
    render: (row) => formatoMonto(row.valorTransaccion)
  },
  // 11. Cod. Error
  { 
    header: "Cód. Error", className: "whitespace-normal", 
    accessor: "codigoError", 
    type: "text", 
    width: "60px", 
    cellClass: "text-xs text-left text-red-600 font-bold pl-2" 
  },
  // 12. Mensaje (Ancho grande para leer el texto)
  //{ 
  //  header: "Mensaje", 
  //  accessor: "descricaoMensaje", 
  //  type: "text", 
  //  width: "180px", 
  //  cellClass: "text-xs text-gray-600 truncate max-w-[180px]", 
  //  render: (row) => <span title={row.descricaoMensaje}>{row.descricaoMensaje}</span>
  //},
  // 12. Desc. Clave de Mensaje
{
  header: "Desc. Clave de Mensaje",
  accessor: "descricaoMsgDsckey",
  type: "text",
  width: "180px",
  cellClass: "text-xs text-gray-600 truncate max-w-[180px]",
  render: (row) => (
    <span title={row.descricaoMsgDsckey}>
      {row.descricaoMsgDsckey}
    </span>
  )
},
  // 13. Tipo Transacción
  { 
    header: "Tipo Transacción", 
    accessor: "codigoTipoTransaccion", 
    type: "text", 
    width: "150px", 
    cellClass: "text-xs text-left font-bold text-blue-700 whitespace-nowrap pl-2" 
  },
  // 14. Acción
  {
    header: "Acción", 
    type: "action", 
    width: "60px",
    render: (row) => (
        <button 
          className="text-gray-400 hover:text-blue-600 transition-colors p-1" 
          title="Ver Detalle"
          onClick={() => setIdSeleccionado(row.id)}
        >
          <IconEye /> 
        </button>
    )
  }
];

  return (
    <div className="page">
      <ListHeader
        title="Transacciones Bancard"
        filters={filtros}
        onClearFilters={() => {
          setFiltros({});            // Borra filtros del estado
          setPaginaActual(1);        // Vuelve a pag 1
          fetchList(0, porPagina, {}); // Recarga la lista limpia
        }}
        onFilterClick={() => setMostrarFiltros(true)}
        showFilterButton={true}
        showAdd={false}
        pageSize={porPagina}
        onPageSizeChange={manejarCambioCantidad}
      />

      {/* DRAWER DE FILTROS */}
      {mostrarFiltros && (
        <FiltroTransaccionesBancard
          initialFilters={filtros}
          onSubmit={(nuevosFiltros) => {
            setFiltros(nuevosFiltros);     // 1. Guardamos filtros en estado
            setPaginaActual(1);            // 2. Reseteamos a página 1
            fetchList(0, porPagina, nuevosFiltros); // 3. Recargamos lista
            setMostrarFiltros(false);      // 4. Cerramos el drawer
          }}
          onClose={() => setMostrarFiltros(false)}
        />
      )}

      {/* MODAL VER DETALLE */}
      {idSeleccionado && (
        <VerTransaccionesBancard
          id={idSeleccionado}
          onClose={() => setIdSeleccionado(null)}
        />
      )}

      {cargando && (
        <div className="mt-4 text-sm text-center text-gray-600">Cargando transacciones...</div>
      )}

      {!cargando && (
        /* Usamos size XL para forzar el modo desktop extendido o el scroll */
        <DynamicTable 
          size="L" 
          data={items}
          columns={columns}
        />
      )}

      <ListFooter
        page={paginaActual}
        pageSize={porPagina}
        total={totalBackend}
        onPageChange={cambiarPagina}
      />
    </div>
  );
}